import { cloneDeep, isEmpty } from 'lodash'

import { isKubernetesAttack } from 'components/attacks/utils/kubernetes'
import { stepsToGraphAPI } from 'components/scenarios/utils/graph'
import duration from 'duration'
import { Client } from 'types/api/attacks/client'
import { Execution } from 'types/api/attacks/details/execution'
import {
  InfraAttack,
  InfraCommand,
  InfraTarget
} from 'types/api/attacks/details/infra-attack'
import {
  AttackCliArgs,
  AttackTypeCategory,
  InfraStrategyType,
  InfraTargetType
} from 'types/api/attacks/details/targeting'
import { K8sAttack } from 'types/api/kubernetes/attacks'
import { Scenario } from 'types/api/scenarios/scenario'
import { Metadata } from 'types/metadata'
import {
  createIlfiAttackSearchMap,
  createK8AttackSearchMap
} from 'utils/searchStringGenerate'

const ATTACK_TYPE_DEPENDENCIES = {
  cpu: [],
  disk: ['cpu'],
  io: ['cpu'],
  memory: ['cpu'],
  process_killer: ['cpu'],
  shutdown: ['cpu'],
  time_travel: ['cpu'],
  blackhole: ['cpu', 'latency'],
  dns: ['cpu', 'latency'],
  latency: ['cpu'],
  packet_loss: ['cpu', 'latency']
}

export function areAttackDependenciesHealthy(
  attackType: string,
  target: Client
): boolean {
  const attackTypeDependencies = ATTACK_TYPE_DEPENDENCIES[attackType]
  let attackValidationResults = []

  if (target.validation_results) {
    attackValidationResults = target.validation_results?.results.filter(
      (result) =>
        attackTypeDependencies?.includes(result.attack_type) &&
        result.status === 'ERROR'
    )
  }

  if (attackValidationResults.length > 0) {
    return false
  }

  return true
}

export function getResolvedHosts(attack: InfraAttack): string[] {
  return attack?.infra_target?.resolvedHosts ?? attack?.targets ?? []
}

export function getResolvedContainers(attack: InfraAttack): string[] {
  return (
    attack?.infra_target?.resolvedContainers ?? attack?.target_containers ?? []
  )
}

/**
 * Informs initial parameters for attack creation form.
 * @param  {object} scenarioAttack Last attack step previously selected.
 * @return {object} Formated attack to be compatible with
 *  createInfraInitialTarget function in infraInitialFormState.js.
 */
export function scenarioAttackToAdHocAttack(scenarioAttack: {
  targetDefinition: InfraTarget
  impactDefinition: InfraCommand
  metrics: Record<string, unknown>
}): Record<string, unknown> {
  const { targetDefinition, impactDefinition, metrics } = scenarioAttack
  let targetAll = false

  // Depending on `targetType`, we check whether `tags` or `labels` are empty,
  // and pass through our UI-only `targetAll` param to the state we provide our
  // InfraAttackForm to correctly toggle the `<InputSwitch />` so users are not blocked from rerunning attacks
  if (
    targetDefinition.strategyType !== InfraStrategyType.Exact &&
    ((targetDefinition.targetType === InfraTargetType.Host &&
      isEmpty(targetDefinition.strategy.tags) &&
      isEmpty(targetDefinition.strategy.multiSelectTags)) ||
      (targetDefinition.targetType === InfraTargetType.Container &&
        isEmpty(targetDefinition.strategy.labels) &&
        isEmpty(targetDefinition.strategy.multiSelectLabels)))
  ) {
    targetAll = true
  } else if (targetDefinition.strategyType === 'Exact') {
    // when we create a scenario from an attack with exact targeting,
    // we want to reset that to Random targeting (Scenarios don't do exact)
    targetDefinition.strategyType = 'Random'
    targetDefinition.strategy.type = 'Random'
    // and we want to be sure we turn off target all so the user has to select new targets
    targetAll = false
    // In the future, if there's time, we could maybe make an attempt at inferring a tag
    // from the exact targets
  }

  const convertedAttack = {
    targetAll, // FE only
    args: impactDefinition.commandArgs.cliArgs,
    providers: impactDefinition.commandArgs.providers,
    trafficImpactMapping: impactDefinition.commandArgs.trafficImpactMapping,
    target_type: targetDefinition.targetType,
    infra_command: {
      commandType: impactDefinition.commandType
    },
    infra_target: {
      strategy: targetDefinition.strategy,
      strategyType: targetDefinition.strategyType,
      targetType: targetDefinition.targetType
    },
    metrics
  }

  return convertedAttack
}

/**
 * Take the data for an attack, or an execution of an attack, and convert that into the format
 * used on the create scenario form
 *
 * @param execution object
 * @param name string
 * @param description string
 * @param hypothesis string
 * @returns object
 */
export function convertAttackToScenarioState(
  execution: Partial<Execution | InfraAttack>,
  name: string,
  description: string,
  hypothesis: string
): { scenario: Partial<Scenario> } {
  const steps = [
    {
      delay: 5,
      attacks: [
        {
          impactDefinition: {
            commandArgs: {
              cliArgs: execution?.args ?? [],
              trafficImpactMapping: execution.traffic_impact_mapping,
              providers: execution.providers ?? []
            },
            commandType: execution?.infra_command?.commandType ?? ''
          },
          targetDefinition: {
            targetType: execution?.infra_target?.targetType ?? '',
            strategy: execution?.infra_target?.strategy ?? {
              percentage: 100
            },
            strategyType: execution?.infra_target?.strategyType ?? 'Random'
          }
        }
      ]
    }
  ]

  return {
    scenario: {
      name,
      description,
      hypothesis,
      steps,
      graph: stepsToGraphAPI(steps)
    }
  }
}

/**
 * Checks if an attack is a network attack
 *
 * @param attack {} An attack data object
 * @param metadata [] attack metadata list
 * @returns bool
 */
export function isNetworkAttack(
  attack: InfraAttack,
  metadata: Metadata
): boolean {
  return Boolean(
    attack?.infra_command?.commandType &&
      metadata?.find(
        (attackMeta) =>
          attackMeta.name === attack.infra_command.commandType &&
          attackMeta.type === AttackTypeCategory.Network
      )
  )
}

/**
 * Filter IPs out of a comma-separated string of IPs
 *
 * @param ipList string A comma-separated list of IPs
 * @param removeIPs string[] An array of IPs to remove from the string
 * @returns string
 */
export function filterIPListString(
  ipList: string,
  removeIPs: string[]
): string {
  return ipList
    .split(',')
    .filter((ipAddress) => !removeIPs.includes(ipAddress))
    .join(',')
}

/**
 * loop over all arguments, find ip args, and then filter their IP lists
 *
 * @param attackArgs string[] An array of arguments for the attack
 * @param ipAddressArgs string[] An array of individual argument names
 * @param removeIPs string[] An array of IPs to remove from the string
 * @returns {string[]}
 */
export function filterIPArguments(
  attackArgs: AttackCliArgs,
  ipAddressArgs: string[],
  removeIPs: string[]
): string[] {
  const filteredArgs = Array.from(attackArgs).map((a) => a.toString())

  for (let argNum = 0; argNum < filteredArgs.length; argNum++) {
    if (ipAddressArgs.includes(filteredArgs[argNum])) {
      // this is an ip address arg, so the next arg is the string of IPs we need to filter
      filteredArgs[argNum + 1] = filterIPListString(
        filteredArgs[argNum + 1],
        removeIPs
      )

      // now check if this list of IPs is empty and empty out both args so we can filter the empties out later
      if (!filteredArgs[argNum + 1]) {
        filteredArgs[argNum] = ''
      }

      // now increment past the string arg and continue looping
      // (the user can pass more than one IP address arg via the CLI)
      argNum++
    }
  }

  // remove any empty arguments
  return filteredArgs.filter(Boolean)
}

/**
 * Cleans up completed attack object data when it needs to be reused for an attack rerun or creating a scenario
 *
 * @param attack {} An attack data object
 * @param metadata [] attack metadata list
 * @returns {{}}
 */
export function copyAttackForReuse(
  attack: Record<string, any>,
  metadata: Metadata
): Partial<InfraAttack> {
  const copy = cloneDeep(attack)

  const isHostAttack = attack?.infra_target?.targetType === InfraTargetType.Host
  const isContainerAttack =
    attack?.infra_target?.targetType === InfraTargetType.Container

  // Depending on `targetType`, we check whether `tags/multiSelectTags` or `labels/multiSelectLabels` are empty, and pass through our UI-only `targetAll` param to the state we provide our InfraAttackForm to correctly toggle the `<InputSwitch />` so users are not blocked from rerunning attacks
  if (
    (isHostAttack &&
      isEmpty(attack?.infra_target?.strategy?.tags) &&
      isEmpty(attack?.infra_target?.strategy?.multiSelectTags)) ||
    (isContainerAttack &&
      isEmpty(attack?.infra_target?.strategy?.labels) &&
      isEmpty(attack?.infra_target?.strategy?.multiSelectLabels))
  ) {
    copy.targetAll = true
  }

  // if we're copying an attack that used Host Tags, then we'll have a list of resolved IPs in the IP address arg
  // these need to be removed from the list so that we can resolve fresh IPs the next time the attack runs
  if (isNetworkAttack(attack as InfraAttack, metadata)) {
    copy.trafficImpactMapping = attack.traffic_impact_mapping

    // pull the short and long args for IP address lists from attack metadata so we're not hardcoding them here
    const networkAttack = metadata.find(
      (attackMeta) => attackMeta.name === attack.infra_command.commandType
    )
    const ipAddressArg = networkAttack.args.find((arg) => arg.type === 'IP')
    const ipAddressArgs = [ipAddressArg.short, ipAddressArg.long]

    // check if this network attack has IPs that were resolved from Host Tags, and filter them out
    const ips = attack?.dynamically_resolved_ips

    if (ips) {
      // remove from args (used when re-running an attack)
      if (attack?.args?.length > 0) {
        copy.args = filterIPArguments(copy.args, ipAddressArgs, ips)
      }

      // remove it from this arg list also, just in case
      if (attack?.infra_command?.commandArgs?.cliArgs?.length > 0) {
        copy.infra_command.commandArgs.cliArgs = filterIPArguments(
          copy.infra_command.commandArgs.cliArgs,
          ipAddressArgs,
          ips
        )
      }

      // remove from infra_command parsed args (used when creating a scenario)
      if (attack?.infra_command?.commandArgs?.ipAddresses?.length > 0) {
        copy.infra_command.commandArgs.ipAddresses = filterIPListString(
          attack.infra_command.commandArgs.ipAddresses,
          ips
        )

        if (copy.infra_command.commandArgs.ipAddresses.length < 1) {
          // there's no IPs left, so remove this property entirely
          delete copy.infra_command.commandArgs.ipAddresses
        }
      }
    }
  }

  return copy
}

export function getSearchMapAttack(data: InfraAttack | K8sAttack): string[] {
  if (isKubernetesAttack(data as K8sAttack)) {
    return createK8AttackSearchMap(data)
  }

  return createIlfiAttackSearchMap(data)
}

export function getAttackDurationReadable(
  args: AttackCliArgs = [] as unknown as AttackCliArgs
): string {
  const attackFlagIndex =
    (args.indexOf('-l') > -1 && args.indexOf('-l')) ||
    (args.indexOf('--length') > -1 && args.indexOf('--length'))

  return attackFlagIndex ? duration(args[attackFlagIndex + 1]) : ''
}
